import { getGridBooleanOperators, 
         GridColDef, 
         GridColumnGroupingModel, 
         GridEditInputCell, 
         GridHeaderFilterCellProps, 
         GridRenderCellParams,
         GridRenderEditCellParams,
         GridRenderHeaderFilterProps
       }                                from '@mui/x-data-grid-pro';
import { Typography, 
         Chip,
         Checkbox
       }                                from '@mui/material';
import Textarea                         from '@mui/joy/Textarea';
import { BOOLEAN, 
         FREQUENCY, 
         INSIGHT_ASSOCIATION_LABEL, 
         MEASUREMENT_TYPE, 
         PROGRAM_PARTICIPATION_EXCLUSION_LABEL, 
         RATE_PLAN_DROPDOWN_OPTIONS, 
         RECO_MODEL_ACTION_TYPE, 
         RECO_MODEL_OBJECTIVE, 
         RECO_MODEL_USER_TYPE, 
         TRUTHY_BOOLEAN 
       }                                from 'constants/global';
import { MultiChoiceFilterComponent,
         BooleanObjectFilterComponent,
         SingleChoiceFilterComponent, 
         HomeProfileFilterComponent,
         ApplianceCategoryFilterComponent,
         BooleanFilterComponent
       }                                from './CustomHeaderFilters';
import { getRecoTableColumnsStyles }   from './RecoTableColumnsStyles';
import { arrayToArrayFilterOperator,
         arrayToObjectFilterOperator,
         valueToArrayFilterOperator
       }                                from './CustomFilterOperators';
import { ApplianceMultiSelectEditCell, 
         GroupArrayEditCell, 
         GroupSingleSelectEditCell, 
         HomeProfileEditCell, 
         MultiChoiceEditCell, 
         SingleChoiceEditCell,
         ApplianceCategoryEditCell 
       }                                from './CustomEditCells';
import { RunContextApp }                from 'framework/RCApp';
import { APIData }                      from 'types/cms/frontendTypes';
import { cloneDeep }                    from 'lodash';
import { Localization
       }                                from 'types/apis/recoInterfaces';
import type { RecommendationContentLimits } from 'store/dataReducers/cmsPilotSlice';
import { CMS_DEFAULT_LOCALE }           from 'store/dataReducers/cmsPilotSlice';
import { getChipStyles }                from './RecoTableStyles';
import { CONTENT_EDIT_STATUS }          from 'types/cms/frontendTypes';

// Helper function to get locale-specific value
const getLocaleValue = (reco: APIData<"api::recommendation.recommendation">, fieldName: keyof Localization, selectedLocale: string) => {
  // For English locale, return the main field value
  if (selectedLocale === CMS_DEFAULT_LOCALE) {
    return reco[fieldName] || '';
  }
  
  // For other locales, return the localizations value or fallback to main field
  const localizations: Localization[] = (reco as any).localizations || [];
  const localeData = localizations.find((l: Localization) => l.locale === selectedLocale) || {} as Localization;
  return localeData[fieldName] || '';
};

// Helper function to set locale-specific value
const setLocaleValue = (reco: APIData<"api::recommendation.recommendation">, fieldName: keyof Localization, value: any, selectedLocale: string) => {
  const updatedReco = cloneDeep(reco)
  
  if (selectedLocale === CMS_DEFAULT_LOCALE) {
    updatedReco[fieldName] = value;
  } else {
    // For other locales, update the localizations array
    let localizations: Localization[] = cloneDeep((reco as any).localizations || []);
    const existingIndex = localizations.findIndex((l: Localization) => l.locale === selectedLocale);
    if (existingIndex >= 0) {
      // Update existing locale entry - create new array instead of mutating
      const existing = localizations[existingIndex];
      const updated: Localization = {
        ...existing,
        [fieldName]: value,
      };
      const newLocalizations = [...localizations.slice(0, existingIndex), updated, ...localizations.slice(existingIndex + 1)];
      localizations = newLocalizations;
    } else {
      // Create new locale entry
      const newLocaleEntry: Localization = {
        locale: selectedLocale,
        [fieldName]: value,
      } as Localization;
      localizations.push(newLocaleEntry);
    }

    (updatedReco as any).localizations = localizations
  }
  
  return updatedReco;
};

interface RatePlanCellObject {
  flat : TRUTHY_BOOLEAN
  tier : BOOLEAN
  tou : BOOLEAN
}

const getClassificationCols = (
  applianceCategories : APIData<'api::appliance-category.appliance-category'>[],
  styles: any,
  chipStyles: any
): GridColDef[] => {
 
  return [
    getBooleanColumn('disabled', 'Disabled', 160),
    { 
      field: 'editStatus', 
      headerName: 'Status', 
      width: 150,
      filterOperators : [valueToArrayFilterOperator()],
      renderCell: (params: GridRenderCellParams) => (
        <div style={styles.chipsContainerCellNoScroll}>
          {
            params.value === 'DRAFT' ? (
              <Chip label="Draft" 
                  sx={{ 
                    ...chipStyles.status.draft, 
                    borderRadius: '5px' 
                  }} 
                  size="small" variant="outlined" />
            ) : params.value === 'MODIFIED' ? (
              <Chip label="Modified" 
                  sx={{ 
                    mr: 0.5, 
                    ...chipStyles.status.modified, 
                    borderRadius: '5px'  
                  }} 
                  size="small" variant="outlined" />
            ) : params.row.disabled ? (
              <Chip label="Disabled" 
                  sx={{ 
                    mr: 0.5, 
                    ...chipStyles.status.disabled, 
                    borderRadius: '5px'  
                  }} 
                  size="small" variant="outlined" />
            ) : (
              <Chip label="Published" 
                  sx={{ 
                    mr: 0.5, 
                    ...chipStyles.status.published, 
                    borderRadius: '5px'  
                  }} 
                  size="small" variant="outlined" />
            )
          }
        </div>      
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <SingleChoiceFilterComponent 
                  {...props} 
                  options={[...Object.values(CONTENT_EDIT_STATUS), 'DISABLED']} />
      }
    },
    { 
      field: 'recommendationId', 
      headerName: 'ID', 
      width: 200,
      filterable: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={styles.textCell}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'category', 
      headerName: 'Category', 
      width: 200,
      editable: false,
      filterOperators : [valueToArrayFilterOperator()],
      renderCell: (params) => (
        <Typography variant="body2" 
          sx={styles.textCell}>
          {applianceCategories.find((cat) => cat.categoryId === params.value)!.name}
        </Typography>
      ),
      renderEditCell: (params) => (
        <ApplianceCategoryEditCell 
          {...params} 
          options={applianceCategories} />
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <ApplianceCategoryFilterComponent
                  {...props} 
                  options={applianceCategories.map((cat) => ({id : cat.categoryId, name : cat.name!}))} />
      }
    },
    { 
      field: 'fuelType', 
      headerName: 'Fuel', 
      width: 180,
      editable: false,
      filterOperators : [arrayToArrayFilterOperator()],
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'pre-line', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value.join('\n')}
        </Typography>
      ),
      renderEditCell: (params : GridRenderEditCellParams) => (
        <MultiChoiceEditCell 
          {...params} 
          options={Object.values(MEASUREMENT_TYPE)} />
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <MultiChoiceFilterComponent 
                  {...props} 
                  options={Object.values(MEASUREMENT_TYPE)} />
      }
    },
    { 
      field: 'actionSavingsPotential',
      headerName: 'Action Savings Potential',
      type: 'number',
      width: 200,
      align: 'left',
      headerAlign: 'left',
      editable: true,
      preProcessEditCellProps: (params) => {
        const value = Number(params.props.value);
        const hasError = value < 0 || value > 1 || !Number.isNaN(value);
        return { ...params.props, error: hasError };
      },
      valueGetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params
        if (row.actionSavingsPotential) return row.actionSavingsPotential
        return 0
      },
      valueSetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => { 
        return {
          ...row,
          actionSavingsPotential : params ? parseFloat(params) : 0
        }
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      ),
      renderEditCell: (params) => (
        <GridEditInputCell 
          {...params} 
          inputProps={{
            min: 0,
            max: 1,
            step: 0.1
          }}
        />
      )
    },
    { 
      field: 'frequency', 
      headerName: 'Frequency', 
      width: 180,
      editable: true,
      filterOperators : [valueToArrayFilterOperator()],
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'pre-line', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      ),
      renderEditCell: (params : GridRenderEditCellParams) => (
        <SingleChoiceEditCell 
          {...params} 
          options={Object.values(FREQUENCY)} />
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <SingleChoiceFilterComponent 
                  {...props} 
                  options={Object.values(FREQUENCY)} />
      }
    },
    { 
      field: 'userType', 
      headerName: 'User Type', 
      width: 180,
      editable: true,
      filterOperators : [arrayToArrayFilterOperator()],
      valueGetter: (params : string, row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params.split(',')
        if (row.userType) return row.userType.split(',')
        return []
      },
      valueSetter: (params : string[], row : APIData<'api::recommendation.recommendation'>) => { 
        return {
          ...row,
          userType : params && params.length > 0 ? params.join(',') : ''
        }
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'pre-line', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value && params.value.length > 0 ? params.value.join('\n') : ''}
        </Typography>
      ),
      renderEditCell: (params : GridRenderEditCellParams) => (
        <MultiChoiceEditCell 
          {...params} 
          options={Object.values(RECO_MODEL_USER_TYPE)} />
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <MultiChoiceFilterComponent 
                  {...props} 
                  options={Object.values(RECO_MODEL_USER_TYPE)} />
      }
    },
    { 
      field: 'actionType', 
      headerName: 'Action Type', 
      width: 180,
      editable: true,
      filterOperators : [valueToArrayFilterOperator()],
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'pre-line', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      ),
      renderEditCell: (params : GridRenderEditCellParams) => (
        <SingleChoiceEditCell 
          {...params} 
          options={Object.values(RECO_MODEL_ACTION_TYPE)} />
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <SingleChoiceFilterComponent 
                  {...props} 
                  options={Object.values(RECO_MODEL_ACTION_TYPE)} />
      }
    },
    { 
      field: 'objective', 
      headerName: 'Objective', 
      width: 180,
      editable: true,
      filterOperators : [arrayToArrayFilterOperator()],
      valueGetter: (params : string, row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params.split(',')
        if (row.objective) return row.objective.split(',')
        return []
      },
      valueSetter: (params : string[], row : APIData<'api::recommendation.recommendation'>) => { 
        return {
          ...row,
          objective : params && params.length > 0 ? params.join(',') : ''
        }
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'pre-line', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value && params.value.length > 0 ? params.value.join('\n') : ''}
        </Typography>
      ),
      renderEditCell: (params : GridRenderEditCellParams) => (
        <MultiChoiceEditCell 
          {...params} 
          options={Object.values(RECO_MODEL_OBJECTIVE)} />
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <MultiChoiceFilterComponent 
                  {...props} 
                  options={Object.values(RECO_MODEL_OBJECTIVE)} />
      }
    },
  ]
}

const getContentCols = (
  selectedLocale: string,
  styles: any,
  chipStyles: any,
  recommendationLimits: RecommendationContentLimits
): GridColDef[] => {

  return [
    getTextColumn('title', 'Title', 250, selectedLocale, recommendationLimits.title),
    getTextColumn('description', 'Description', 380, selectedLocale, recommendationLimits.description),
    getTextColumn('ctaButtonText', 'CallToAction', 200, selectedLocale, recommendationLimits.ctaButtonText),
    getTextColumn('ctaButtonLink', 'CTA Link', 200, selectedLocale),
    //getTextColumn('videoLink', 'Video Link', 200, selectedLocale),
    getBooleanColumn('featuredContent', 'Featured', 160),
    getBooleanColumn('program', 'Program', 160),
    //getBooleanColumn('channelPaper', 'Paper', 160),
    getBooleanColumn('channelEmail', 'Email', 160),
    getBooleanColumn('channelWeb', 'Web', 160),
  ]
}

const getRankingCols = (recoRanking : Record<string, number>): GridColDef[] => {

  return [
    { 
      field: 'impact',
      headerName: 'Impact (1-5)',
      type: 'number',
      width: 160,
      align: 'left',
      headerAlign: 'left',
      editable: true,
      preProcessEditCellProps: (params) => {
        const value = Number(params.props.value);
        const hasError = value < 1 || value > 5 || !Number.isInteger(value);
        return { ...params.props, error: hasError };
      },
      valueGetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params
        if (row.score) return (row.score as any).impact
        return 0
      },
      valueSetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => { 
        return {
          ...row,
          score : {
            ...(row.score as any),
            impact : params
          }
        }
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      ),
      renderEditCell: (params) => (
        <GridEditInputCell 
          {...params} 
          inputProps={{
            min: 1,
            max: 5,
            step: 1
          }}
        />
      )
    },
    { 
      field: 'simplicity', 
      headerName: 'Simplicity (1-5)', 
      type: 'number', 
      width: 160,
      align:'left', 
      headerAlign:'left',
      editable: true,
      preProcessEditCellProps: (params) => {
        const value = Number(params.props.value);
        const hasError = value < 1 || value > 5 || !Number.isInteger(value);
        return { ...params.props, error: hasError };
      },
      valueGetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params
        if (row.score) return (row.score as any).simplicity
        return 0
      },
      valueSetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => { 
        return {
          ...row,
          score : {
            ...(row.score as any),
            simplicity : params
          }
        }
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      ),
      renderEditCell: (params) => (
        <GridEditInputCell 
          {...params} 
          inputProps={{
            min: 1,
            max: 5,
            step: 1
          }}
        />
      )
    },
    { 
      field: 'premScore', 
      headerName: 'Preliminary Score', 
      type: 'number', 
      width: 160,
      align:'left', 
      headerAlign:'left', 
      valueGetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params;
        if (row.score) return ((row.score as any).simplicity || 0) + ((row.score as any).impact || 0)
        return 0;
      },
      valueSetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => {
        return {
          ...row,
          score : {
            ...(row.score as any),
            premScore : params
          }
        }
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'rank', 
      headerName: 'Relevance Score', 
      type: 'number', 
      width: 160,
      align:'left', 
      headerAlign:'left',
      valueGetter: (params : any, row : APIData<'api::recommendation.recommendation'>) => {
        return recoRanking[row.recommendationId]?.toFixed(3)
      },
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
          {params.value}
        </Typography>
      )
    },
  ]
}

const getHomeProfileCols = (
    homeProfileMetadata : APIData<'api::home-profile-attribute.home-profile-attribute'>[],
    homeProfile : APIData<'api::utility-home-profile.utility-home-profile'>,
    styles: any,
    chipStyles: any) => {

  const cols : GridColDef[] = [] 

  const disabledAttributes = homeProfile.disabled as Record<string, boolean>

  for (const hpMetadata of homeProfileMetadata) {
    if (disabledAttributes[hpMetadata.attributeId]) continue

    const options = (hpMetadata.values || []) as { id : string, name : string, tag : string }[]

    if (hpMetadata.attributeId === 'postCode') {
      cols.push(getPostCodeColumn(styles, chipStyles))
    } else {
      cols.push(getHomeProfileExclusionColumn(hpMetadata.attributeId, hpMetadata.label, 180, options, styles, chipStyles))
    }
  }

  return cols
}

const getExclusionColumns = (
    rc : RunContextApp,
    homeProfileMetadata : APIData<'api::home-profile-attribute.home-profile-attribute'>[],
    homeProfile : APIData<'api::utility-home-profile.utility-home-profile'>,
    appliances : APIData<'api::appliance.appliance'>[],
    applianceCategories : APIData<'api::appliance-category.appliance-category'>[],
    applianceOwnership : Record<string, any>,
    styles: any,
    chipStyles: any) : GridColDef[] => {

  if (!rc.permissionsManager.canViewRecommendationExclusions()) {
    return []
  }

  return [
    ...getHomeProfileCols(homeProfileMetadata, homeProfile, styles, chipStyles),
    { 
      field: 'appliance', 
      headerName: 'Appliance Ownership', 
      width : 250,
      editable: true,
      filterOperators : [arrayToObjectFilterOperator()],
      valueGetter: (params : Record<string, string[]>, 
                    row : APIData<'api::recommendation.recommendation'>) => {
        if (params) return params
        if (row.applianceProfileExclusion) return row.applianceProfileExclusion
        return {}
      },
      valueSetter : (params : Record<string, string[]>, 
                     row : APIData<'api::recommendation.recommendation'>) => {

        const newParams = {} as Record<string, string[]>
        for (const key in params) {
          const appIds = key.split('|')
          for (const appId of appIds) {
            newParams[appId] = params[key]
          }
        }

        return {
          ...row,
          applianceProfileExclusion : {
            ...row.applianceProfileExclusion as any,
            ...newParams
          }
        }
      },
      renderCell: (params) => {
        const appValues = params.value as Record<string, string[]>
        const tags = []
        for (const key in appValues) {
          for (const value of appValues[key]) {
            const name = appliances.find((app : any) => app.applianceId === key)!.name
            tags.push(`${name} : ${value}`)
          }
        }
        
        return (
          <div style={tags.length > 3 ? styles.chipsContainerCell : styles.chipsContainerCellNoScroll}>
            {tags && tags.map((tag, index) => (
              <Chip key={index} label={tag} 
                sx={{ 
                  mr: 0.5, 
                  ...chipStyles.data
                }} 
                size="small" variant="outlined" />
            ))}
          </div>
        )
      },
      renderEditCell: (params : GridRenderEditCellParams<APIData<'api::recommendation.recommendation'>>) => { 
        
        const groupedOptions = [...Object.values(cloneDeep(applianceOwnership))] 
        groupedOptions.sort((a, b) => a.categoryName.localeCompare(b.categoryName))
        
        return <ApplianceMultiSelectEditCell 
                  {...params} 
                  groupedOptions={groupedOptions}
                  appliances={appliances}/>
      },
    },
    { 
      field: 'ratePlan', 
      headerName: 'Rate Plan', 
      width : 180,
      editable: true,
      filterOperators : [arrayToObjectFilterOperator()],
      valueGetter: (params : RatePlanCellObject, row : any) => {
        if (params) return params;
        if (row.ratePlanExclusion && Object.keys(row.ratePlanExclusion).length > 0) {
          return row.ratePlanExclusion
        }
        return {};
      },
      valueSetter: (params : RatePlanCellObject, row : any) => {
        return {
          ...row,
          ratePlanExclusion : {
            ...row.ratePlanExclusion,
            ...params
          }
        }
      },
      renderCell: (params) => {
        const ratePlan = params.value as RatePlanCellObject
        const tags = []
        if (ratePlan && ratePlan.tier && ratePlan.tier !== 'NONE') tags.push(`Tiered : ${ratePlan.tier}`)
        if (ratePlan && ratePlan.tou && ratePlan.tou !== 'NONE') tags.push(`TOU : ${ratePlan.tou}`)
        if (ratePlan && ratePlan.flat && ratePlan.flat !== 'NONE') tags.push('Flat rate')
        
        return (
          <div style={styles.chipsContainerCellNoScroll}>
            {tags && tags.map((tag, index) => (
              <Chip key={index} label={tag} 
                sx={{ 
                  mr: 0.5, 
                  ...chipStyles.data
                }} 
                size="small" variant="outlined" />
            ))}
          </div>
        )
      },
      renderEditCell: (params : GridRenderEditCellParams) => {      
        return <GroupSingleSelectEditCell 
                  {...params} 
                  groupedOptions={RATE_PLAN_DROPDOWN_OPTIONS} 
                  defaultValue="NONE"/>
      },
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        const options = [{
          type: 'tier',
          value: 'YES',
          label: 'Tiered - Yes'
        }, {
          type: 'tier',
          value: 'NO',
          label: 'Tiered - No'
        }, {
          type: 'tou',
          value: 'YES',
          label: 'TOU - Yes'
        }, {
          type: 'tou',
          value: 'NO',
          label: 'TOU - No'
        }, {
          type: 'flat',
          value: 'YES',
          label: 'Flat - Yes'
        }]
        return <BooleanObjectFilterComponent
          {...props}
          options={options}
        />
      }
    },
    {
      field: 'programParticipation',
      headerName: 'Program Participation',
      width: 300,
      editable: true,
      filterOperators : [arrayToObjectFilterOperator()],
      valueGetter: (params : any, row : any) => {
        if (params) return params
        if (row.ppExclusion) return row.ppExclusion
        return {}
      },
      valueSetter : (params, row) => {
        return {
          ...row,
          ppExclusion : {
            ...row.ppExclusion,
            ...params
          }
        }
      },
      renderCell: (params) => {
        const programParticipation = params.value as Record<string, string[]>
        const tags = []
        for (const key in programParticipation) {
          if (programParticipation[key].length === 0) continue
          const values = programParticipation[key] as string[]
          tags.push(`${(PROGRAM_PARTICIPATION_EXCLUSION_LABEL as any)[key]} : ${values.join(', ')}`)
        }
        return (
          <div style={tags.length > 3 ? styles.chipsContainerCell : styles.chipsContainerCellNoScroll}>
            {tags && tags.map((tag, index) => (
              <Chip key={index} label={tag} 
                sx={{ 
                  mr: 0.5, 
                  ...chipStyles.data
                }} 
                size="small" variant="outlined" />
            ))}
          </div>
        )
      },
      renderEditCell: (params : GridRenderEditCellParams) => {
        return (  
          <GroupArrayEditCell
            {...params}
            groupedOptions={Object.keys(PROGRAM_PARTICIPATION_EXCLUSION_LABEL).map((key) => ({
              group : key,
              label : (PROGRAM_PARTICIPATION_EXCLUSION_LABEL as any)[key]
            }))}
          />
        )
      }
    },
    {
      field: 'insightAssociation',
      headerName: 'Insights Association',
      width: 300,
      editable: true,
      filterOperators : [arrayToObjectFilterOperator()],
      valueGetter: (params : any, row : any) => {
        if (params) return params
        if (row.insightAssociation) return row.insightAssociation
        return {}
      },
      valueSetter: (params : any, row : any) => {
        return {
          ...row,
          insightAssociation : {
            ...row.insightAssociation,
            ...params
          }
        }
      },
      renderCell: (params) => {
        const insightAssociation = params.value as Record<string, string[]>
        const tags = []
        for (const key in insightAssociation) {
          if (insightAssociation[key].length === 0) continue
          const values = insightAssociation[key] as string[]
          for (const value of values) {
            const categoryName = applianceCategories.find((cat) => cat.categoryId === value)!.name
            tags.push(`${(INSIGHT_ASSOCIATION_LABEL as any)[key]} : ${categoryName}`)
          }
        }
        return (
          <div style={tags.length > 3 ? styles.chipsContainerCell : styles.chipsContainerCellNoScroll}>
            {tags && tags.map((tag, index) => (
              <Chip key={index} label={tag} 
                sx={{ 
                  mr: 0.5, 
                  ...chipStyles.data
                }} 
                size="small" variant="outlined" />
            ))}
          </div>
        )
      },
      renderEditCell: (params : GridRenderEditCellParams) => {
        return (  
          <GroupArrayEditCell
            {...params}
            groupedOptions={Object.keys(INSIGHT_ASSOCIATION_LABEL).map((key) => ({
              group : key,
              label : (INSIGHT_ASSOCIATION_LABEL as any)[key],
              children : applianceCategories.map((cat) => ({
                id : cat.categoryId,
                label : cat.name!
              }))
            }))}
          />
        )
      }
    } 
  ]
}

export const columnDefinition = (
    rc : RunContextApp, 
    homeProfileMetadata : APIData<'api::home-profile-attribute.home-profile-attribute'>[],
    homeProfile : APIData<'api::utility-home-profile.utility-home-profile'>,
    appliances : APIData<'api::appliance.appliance'>[],
    applianceCategories : APIData<'api::appliance-category.appliance-category'>[],
    applianceOwnership : Record<string, any>,
    recoRanking : Record<string, number>,
    selectedLocale : string,
    theme : any,
    recommendationLimits : RecommendationContentLimits
  ) : {
      columnGroupingModel: GridColumnGroupingModel,
      columns: GridColDef[]
    } => {

  const styles = getRecoTableColumnsStyles(theme);
  const columnGroupingModel: GridColumnGroupingModel = []
  
  const chipStyles = getChipStyles(theme);
  
  const classificationCols = getClassificationCols(applianceCategories, styles, chipStyles)
  columnGroupingModel.push({
    groupId: 'Classification',
    description: '',
    children: [
      ...classificationCols.map((col) => ({field: col.field}))
    ],
  })

  const contentCols = getContentCols(selectedLocale, styles, chipStyles, recommendationLimits)
  columnGroupingModel.push({
    groupId: 'Content',
    description: '',
    children: [
      ...contentCols.map((col) => ({field: col.field}))
    ],
  })

  const rankingCols = getRankingCols(recoRanking)
  columnGroupingModel.push({
    groupId: 'Score',
    description: '',
    children: [
      ...rankingCols.map((col) => ({field: col.field}))
    ],
  })

  const exclusionCols = getExclusionColumns(rc, homeProfileMetadata, homeProfile, 
      appliances, applianceCategories, applianceOwnership, styles, chipStyles)
  if (exclusionCols.length > 0) {
    columnGroupingModel.push({
      groupId: 'Exclusions',
      description: '',
      children: [
        ...exclusionCols.map((col) => ({field: col.field}))
      ],
    })
  }

  return {
    columnGroupingModel,
    columns: [
      ...classificationCols,
      ...contentCols,
      ...rankingCols,
      ...exclusionCols
    ]
  }
} 

const getTextColumn = (field : string, headerName : string, width : number, selectedLocale: string, maxLength?: number) : GridColDef => {
  // Only apply locale logic to string fields that can be localized
  const localizableFields: (keyof Localization)[] = ['title', 'description', 'detail', 'ctaButtonText', 'ctaButtonLink', 'videoLink'];
  const isLocalizable = localizableFields.includes(field as keyof Localization);
  
  return {
    field: field, 
    headerName: headerName + (maxLength ? ` (${maxLength} chars)` : ''), 
    width: width, 
    editable: true,
    preProcessEditCellProps: (params) => {
      const value = params.props.value
      if (maxLength && value.length > maxLength) {
        // show error toast message
        return { ...params.props, error: true };
      }
      return { ...params.props, error: false };
    },
    valueGetter: isLocalizable ? (params: any, row : APIData<'api::recommendation.recommendation'>) => {
      return getLocaleValue(row, field as keyof Localization, selectedLocale);
    } : undefined,
    valueSetter: isLocalizable ? (params: any, row: APIData<'api::recommendation.recommendation'>) => {
      return setLocaleValue(row, field as keyof Localization, params, selectedLocale);
    } : undefined,
    renderCell: (params) => (
      <Typography variant="body2" 
        style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
        {isLocalizable ? getLocaleValue(params.row, field as keyof Localization, selectedLocale) : params.value}
      </Typography>
    ),
    renderEditCell: (params : GridRenderEditCellParams<APIData<'api::recommendation.recommendation'>>) => {
      const charCount = (params.value || '').length;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          <Textarea
            style={{ 
              border: 'none', 
              width: "100%", 
              fontSize: "14px", 
              fontWeight: 400, 
              lineHeight: "22px",
              whiteSpace: 'normal', 
              wordBreak: 'break-word',
              padding: '8px',
              borderRadius: '4px',
              color: 'inherit',
              backgroundColor: 'inherit',
              flex: 1,
            }}
            value={params.value || ''}
            onChange={(event) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: event.target.value
              });
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          {maxLength && (
            <Typography variant="caption" 
              style={{ 
                color: charCount > maxLength ? '#d32f2f' : '#999', 
                textAlign: 'right',
                fontSize: '11px',
                lineHeight: '16px',
                paddingRight: '8px',
                paddingBottom: '2px',
              }}>
              {charCount} / {maxLength}
            </Typography>
          )}
        </div>
      );
    }
  }
}

const getBooleanColumn = (field : string, headerName : string, width : number) : GridColDef => {
  return {
    field: field, 
    headerName: headerName, 
    width: width, 
    type: 'boolean',
    align: 'left',
    headerAlign: 'left',
    filterOperators : getGridBooleanOperators(),
    editable: true,
    renderHeaderFilter : (props : GridRenderHeaderFilterProps) => (
      <BooleanFilterComponent 
        {...props}
      />
    ),
    renderCell: (params) => (
      <Checkbox
        checked={params.value}
        disabled
        size="small"
        sx={{ padding: '0px' }}
      />
    )
  }
}

const getPostCodeColumn = (styles: any, chipStyles: any): GridColDef => {

  return { 
    field: 'postalCode', 
    headerName: 'Post Code', 
    width: 160,
    align:'left', 
    headerAlign:'left',
    editable: true,
    valueGetter: (params : string, row : any) => {
      return params || row.homeProfileExclusion['postalCode'] || '';
    },
    valueSetter: (params : string, row : any) => {
      return {
        ...row,
        homeProfileExclusion : {
          ...row.homeProfileExclusion,
          postalCode : params
        }
      }
    },
    renderCell: (params) => (
      <Typography variant="body2" 
        style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
        {params.value}
      </Typography>
    ),
    renderEditCell: (params : GridRenderEditCellParams<APIData<'api::recommendation.recommendation'>>) => (
      <Textarea
        style={{ 
          border: 'none', 
          width: "100%", 
          fontSize: "14px", 
          fontWeight: 400, 
          lineHeight: "22px",
          whiteSpace: 'normal', 
          wordBreak: 'break-word',
          padding: '8px',
          borderRadius: '4px',
          color: 'inherit',
          backgroundColor: 'inherit',
        }}
        value={params.value || ''}
        onChange={(event) => {
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: event.target.value
          });
        }}
        autoFocus
        onClick={(e) => e.stopPropagation()}
      />
    )
  }
}

const getHomeProfileExclusionColumn = (field : string, headerName : string, 
                                       width : number, options : { id : string, name : string, tag : string }[], styles: any, chipStyles: any) : GridColDef => {
                                        
  return { 
    field: field, 
    headerName: headerName, 
    width : width,
    editable: true,
    filterOperators : [arrayToArrayFilterOperator()],
    valueGetter: (params : string[], row : any) => {
      return params || row.homeProfileExclusion[field] || [];
    },
    valueSetter: (params : string[], row : any) => {
      return {
        ...row,
        homeProfileExclusion : {
          ...row.homeProfileExclusion,
          [field] : params
        }
      }
    },
    renderCell: (params) => {
      const ids = params.value || [];
      const tagThreshold = params.field === 'weather' ? 9 : 3
      const showScroll = ids.length > tagThreshold

      return (
        <div style={showScroll ? styles.chipsContainerCell : styles.chipsContainerCellNoScroll}>
          {ids.map((id : string, index : number) => (
            <Chip key={index} label={options.find((option : any) => option.id === id)!.name}
              sx={{ 
                mr: 0.5, 
                ...chipStyles.data
              }}
              size="small" variant="outlined" />
          ))}
        </div>
      );
    },
    renderEditCell: (params) => (
      <HomeProfileEditCell 
        {...params} 
        options={options} />
    ),
    renderHeaderFilter: (props: GridHeaderFilterCellProps) => (
      <HomeProfileFilterComponent
        {...props}
        options={options}
      />
    ),
  }

}